import React, { useState, useEffect, useMemo, useRef } from "react";
import axiosInstance, { weburl } from "../../../axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingScreen from "../../components/LoadingScreen";
import { useThemeContext } from "../../../fetchdata/ThemeContext";

const OrderQueeViewFloor = () => {
  const { sendMessage, allMessages } = useThemeContext();

  const [currentDate, setCurrentDate] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [currentMinutes, setCurrentMinutes] = useState(null);

  const [Quee, setQuee] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { hospital, floor } = useParams();
  const navigate = useNavigate();

  // ---- Helper to convert "HH:mm" to minutes since midnight ----
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const toDateOnly = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const norm = (v) => String(v ?? "").toLowerCase().trim();

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const dateOptions = { weekday: "short", year: "numeric", month: "short", day: "numeric" };
      const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

      setCurrentDate(now.toLocaleDateString(undefined, dateOptions));
      setCurrentTime(now.toLocaleTimeString(undefined, timeOptions));
      setCurrentMinutes(now.getHours() * 60 + now.getMinutes());
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const getUserQuee = async () => {
    try {
      const { data } = await axiosInstance.get(`/quee/${hospital}/${floor ?? ""}`);
      if (data?.success) setQuee(data);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      toast.error("order Not found");
    }
  };

  useEffect(() => {
    getUserQuee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // Resolve Floor + Room (same as before)
  // -----------------------------
  const resolveFloorRoom = (order) => {
    const fStr = floor ?? "";
    const hosKey = order?.hosId?._id;

    let matched = null;

    if (Array.isArray(order?.addProduct) && order.addProduct.length) {
      matched =
        order.addProduct.find((p) => {
          const pf = p?.floor ?? p?.employee?.data?.hospitalMeta?.[hosKey]?.floor;
          if (pf === undefined || pf === null) return false;
          return String(pf).trim() === String(fStr).trim();
        }) || null;
    }

    const matchedFloor =
      matched?.floor ??
      matched?.employee?.data?.hospitalMeta?.[hosKey]?.floor ??
      order?.employeeId?.hospitalMeta?.[hosKey]?.floor ??
      order?.floor ??
      null;

    const matchedRoom =
      matched?.room ??
      matched?.employee?.data?.hospitalMeta?.[hosKey]?.room ??
      order?.myroom ??
      order?.employeeId?.hospitalMeta?.[hosKey]?.room ??
      null;

    return { floor: matchedFloor, room: matchedRoom };
  };

  // ============================================================
  // ✅ IMPORTANT FIX:
  // socket emits orderId = mongo _id sometimes (Order._id)
  // Floor board uses token orderId (Order.orderId)
  // so we map mongoId -> tokenId, and always work with tokenId.
  // ============================================================

  const mongoIdToTokenMap = useMemo(() => {
    const map = new Map();
    (Quee?.orders || []).forEach((o) => {
      if (o?._id && o?.orderId) map.set(String(o._id), String(o.orderId));
    });
    return map;
  }, [Quee]);

  const normalizeEventOrderToken = (m) => {
    const raw = String(m?.orderId ?? "");
    if (!raw) return "";
    // if message orderId is mongo id, convert to token
    if (mongoIdToTokenMap.has(raw)) return mongoIdToTokenMap.get(raw);
    // else it might already be token
    return raw;
  };

  // 1) Only events relevant to this hospital+floor board
  const boardEvents = useMemo(() => {
    const f = String(floor ?? "");
    const hos = String(hospital ?? "");

    return (allMessages || [])
      .filter((m) => {
        if (!m) return false;
        if (!m.orderId) return false;

        // match hos+floor only if present
        if (m.hosId != null && String(m.hosId) !== hos) return false;
        if (m.floor != null && String(m.floor) !== f) return false;

        return true;
      })
      .map((m) => ({
        ...m,
        __tokenOrderId: normalizeEventOrderToken(m), // ✅ token id for matching
      }))
      .filter((m) => !!m.__tokenOrderId);
  }, [allMessages, hospital, floor, mongoIdToTokenMap]);

  // 2) Removed Orders (cancel/complete/remove)
  const removedTokenIds = useMemo(() => {
    const s = new Set();

    boardEvents.forEach((m) => {
      const st = norm(m.subType);
      if (st === "cancel" || st === "complete" || st === "remove") {
        s.add(String(m.__tokenOrderId));
      }
    });

    // also remove from API status if exists
    (Quee?.orders || []).forEach((o) => {
      if (!o?.orderId) return;
      if (o.status === 1 || o.status === 2) s.add(String(o.orderId));
    });

    return s;
  }, [boardEvents, Quee]);

  // 3) HOLD state per tokenId
  const holdStateByTokenId = useMemo(() => {
    const map = new Map();

    // seed from API
    (Quee?.orders || []).forEach((o) => {
      if (!o?.orderId) return;
      const st = norm(o.subType);
      if (st === "hold" || st === "unhold") map.set(String(o.orderId), st);
    });

    // override from socket (last wins by arrival)
    boardEvents.forEach((m) => {
      const st = norm(m.subType);
      if (st === "hold" || st === "unhold") map.set(String(m.__tokenOrderId), st);
    });

    return map;
  }, [Quee, boardEvents]);

  // 4) PRIORITY state per tokenId
  const priorityByTokenId = useMemo(() => {
    const map = new Map();

    // seed from API
    (Quee?.orders || []).forEach((o) => {
      if (!o?.orderId) return;
      const st = norm(o.subType);
      if (st === "priority") map.set(String(o.orderId), true);
      if (st === "unpriority") map.set(String(o.orderId), false);
    });

    // override from socket
    boardEvents.forEach((m) => {
      const st = norm(m.subType);
      if (st === "priority") map.set(String(m.__tokenOrderId), true);
      if (st === "unpriority") map.set(String(m.__tokenOrderId), false);
    });

    return map;
  }, [Quee, boardEvents]);

  // ============================================================
  // ✅ MERGE API + SOCKET "ADD" (keep existing behavior)
  // ============================================================
  const filteredOrders = useMemo(() => {
    if (!Quee?.orders || currentMinutes === null) return [];

    const today = toDateOnly(new Date());
    const fStr = String(floor ?? "");

    // socket "add" messages
    const addedMessages = boardEvents.filter((m) => norm(m.subType) === "add");

    // merge orderId unique using TOKEN id
    const orderMap = new Map();

    (Quee.orders || []).forEach((order) => {
      if (order?.orderId) orderMap.set(String(order.orderId), order);
    });

    // add messages must also use token id as key
    addedMessages.forEach((msg) => {
      const tokenId = String(msg.__tokenOrderId);
      if (tokenId) orderMap.set(tokenId, msg);
    });

    const finalOrders = Array.from(orderMap.values());

    return finalOrders
      .filter((order) => {
        if (!order?.orderId) return false;

        const tokenId = String(order.orderId);

        // ✅ CANCEL/COMPLETE/REMOVE
        if (removedTokenIds.has(tokenId)) return false;

        // ✅ HOLD
        const holdState = holdStateByTokenId.get(tokenId);
        if (holdState === "hold") return false;

        // ✅ date filter today
        const orderDate = toDateOnly(order.date);
        if (orderDate !== today) return false;

        // ✅ floor filter for add message
        if (norm(order?.subType) === "add" && order?.date) {
          if (order.floor != null && String(order.floor) !== fStr) return false;
          const msgDate = toDateOnly(order.date);
          if (msgDate !== today) return false;
        }

        // ✅ addProduct floor filter
        if (Array.isArray(order?.addProduct) && order.addProduct.length) {
          const hosKey = order?.hosId?._id;
          const anyHasFloor = order.addProduct.some((p) => p?.floor !== undefined && p?.floor !== null);

          if (anyHasFloor) {
            const hasThisFloor = order.addProduct.some((p) => {
              const pf = p?.floor ?? p?.employee?.data?.hospitalMeta?.[hosKey]?.floor;
              if (pf === undefined || pf === null) return false;
              return String(pf).trim() === fStr.trim();
            });
            if (!hasThisFloor) return false;
          }
        }

        // ✅ time filter
        const start = parseTimeToMinutes(order.Ltime);
        const end = parseTimeToMinutes(order.Etime);

        if (start === null && end === null) return true;
        if (start === null || end === null) return false;

        return currentMinutes >= start && currentMinutes <= end;
      })
      .sort((a, b) => {
        // ✅ PRIORITY on top
        const ap = priorityByTokenId.get(String(a.orderId)) === true ? 1 : 0;
        const bp = priorityByTokenId.get(String(b.orderId)) === true ? 1 : 0;
        if (ap !== bp) return bp - ap;

        return new Date(a.createdAt) - new Date(b.createdAt);
      });
  }, [Quee, currentMinutes, floor, boardEvents, removedTokenIds, holdStateByTokenId, priorityByTokenId]);

  // uniqueness by employee (unchanged), with room resolved
  const uniqueOrders = useMemo(() => {
    const base = (filteredOrders || []).map((o) => {
      const fr = resolveFloorRoom(o);
      return { ...o, _floorResolved: fr.floor, _roomResolved: fr.room };
    });

    return base.filter(
      (order, index, self) =>
        index ===
        self.findIndex((o) => {
          const a = typeof o.employeeId === "object" ? o.employeeId._id : o.employeeId;
          const b = typeof order.employeeId === "object" ? order.employeeId._id : order.employeeId;
          return a === b;
        })
    );
  }, [filteredOrders]);

  // marquee
  const innerRef = useRef(null);
  const [duration, setDuration] = useState(20);

  useEffect(() => {
    if (!innerRef.current) return;
    const width = innerRef.current.scrollWidth / 2;
    const speed = 80;
    setDuration(width / speed);
  }, [Quee?.hospital?.subDepartments]);

  return (
    <>
      <main className="w-100">
        {isLoading ? (
          <LoadingScreen />
        ) : (
          <>
            <div className="w-100">
              {/* HEADER (unchanged) */}
              <header className="board-header mb-4 rounded-0">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <img
                      style={{ width: 50, height: 50, color: "#0d47a1", fontWeight: 700 }}
                      className="rounded bg-light d-flex align-items-center justify-content-center"
                      src={`${weburl}${Quee?.hospital?.profile}`}
                      alt="Hospital"
                    />
                    <div>
                      <div className="clinic-name">
                        {Quee?.hospital?.username} - {floor ?? "-"}
                      </div>

                      <div className="tagline d-flex gap-2">
                        <span className="d-block"> Live Queue – </span>

                        <div className="marquee">
                          <div className="marquee__inner" ref={innerRef} style={{ animationDuration: `${duration}s` }}>
                            {[...(Quee?.hospital?.subDepartments || []), ...(Quee?.hospital?.subDepartments || [])].map(
                              (item, index) => (
                                <span key={index} className="bg-dark text-white px-2 py-1 rounded me-3">
                                  {item}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="time-box">
                    <div>{currentDate}</div>
                    <div className="time">{currentTime}</div>
                  </div>
                </div>
              </header>

              {/* TABLE (unchanged UI) */}
              <table className="table table-bordered border-primary align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center bg-secondary">
                      <div className="m-0 h1 fw-black">Token</div>
                    </th>
                    <th className="text-center bg-secondary">
                      <div className="m-0 h1 fw-black">Room</div>
                    </th>
                    <th className="text-center bg-white">
                      <div className="m-0 h1 fw-black">Token</div>
                    </th>
                    <th className="text-center bg-white">
                      <div className="m-0 h1 fw-black">Room</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {Array.from({ length: 6 }).map((_, i) => {
                    const first = uniqueOrders[i];
                    const second = uniqueOrders[i + 6];

                    const firstRoom =
                      first?._roomResolved ??
                      first?.myroom ??
                      first?.employeeId?.hospitalMeta?.[first?.hosId?._id]?.room ??
                      "--";

                    const secondRoom =
                      second?._roomResolved ??
                      second?.myroom ??
                      second?.employeeId?.hospitalMeta?.[second?.hosId?._id]?.room ??
                      "--";

                    return (
                      <tr key={i}>
                        <td className="text-center bg-secondary">
                          <div className="m-0 h1 fw-black">{first?.orderId ?? "--"}</div>
                        </td>

                        <td className="text-center bg-secondary">
                          <div className="m-0 h1 fw-black">{firstRoom}</div>
                        </td>

                        <td className="text-center bg-white">
                          <div className="m-0 h1 fw-black">{second?.orderId ?? "--"}</div>
                        </td>

                        <td className="text-center bg-white">
                          <div className="m-0 h1 fw-black">{secondRoom}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </>
  );
};

export default OrderQueeViewFloor;
